import type { vitality as en } from '@/i18n/locales/en/vitality';
import type { Translation } from '@/i18n/types';

export const vitality: Translation<typeof en> = {
  page: {
    title: 'Vitalité',
    description: 'Tendances de bien-être anonymisées et indicatives, au niveau du groupe.',
    noScope: 'Sélectionnez une entreprise pour voir ses chiffres de vitalité.',
    noDepartments: "Aucun département ici n'a de statistiques que vous pouvez voir.",
    disclaimer:
      'Tous les chiffres sont <strong>des indicateurs de bien-être indicatifs</strong> et <strong>ne constituent pas un diagnostic médical</strong>. Les tendances ne sont affichées que pour un nombre suffisant de participants (protection de la vie privée dès la conception).',
  },
  filters: {
    title: 'Filters',
    department: {
      label: 'Département',
    },
    location: {
      label: 'Site',
      all: 'Tous les sites',
    },
  },
  badge: {
    measured: 'Mesuré',
    aiDerived: 'Analyse IA',
  },
  level: {
    good: 'Bon',
    opportunity: 'À améliorer',
  },
  statusPill: {
    healthy: 'Niveau sain',
    opportunity: "Occasion d'aider",
    strongForward: 'Fort et en hausse',
  },
  trend: {
    flat: 'Aucun changement',
    vsPrevious: 'vs période précédente',
    vsAverage: 'vs moyenne de la période',
    up: 'En hausse',
    down: 'Légèrement plus bas',
    upBad: 'Légèrement plus élevé',
    week: 'sem.',
  },
  units: {
    hoursMinutes: '{{h}}h {{m}}m',
    average: 'moy. {{value}}',
  },
  status: {
    noData: 'Aucune donnée sur cette période.',
  },
  table: {
    level: 'Niveau',
    trend: 'Tendance',
    action: 'Action',
    actionNone: '—',
    actionLink: 'Département',
    actionLinkTitle: 'Aller à ce département',
  },
  insight: {
    title: 'Ce que disent les chiffres de vitalité',
    unavailable: 'Pas encore assez de données pour un résumé.',
    vitality: {
      up: 'La vitalité augmente ({{pct}}% {{period}}) avec un taux de participation de {{participation}}%.',
      down: 'La vitalité diminue ({{pct}}% {{period}}) avec un taux de participation de {{participation}}%.',
      flat: 'La vitalité est stable ({{pct}}% {{period}}) avec un taux de participation de {{participation}}%.',
      noParticipation: 'La vitalité est stable ({{pct}}% {{period}}).',
    },
    period: {
      last7Days: 'ces 7 derniers jours',
      last30Days: 'ce mois-ci',
      thisWeek: 'cette semaine',
      thisMonth: 'ce mois-ci',
      custom: 'sur cette période',
    },
    stress: {
      up: 'Le stress augmente ({{pct}}%)',
      down: 'le stress diminue ({{pct}}%)',
      flat: 'le stress reste stable',
    },
    sleep: {
      longer: 'et les employés dorment en moyenne {{minutes}} min de plus.',
      shorter: 'et les employés dorment en moyenne {{minutes}} min de moins.',
      same: 'et les employés dorment à peu près autant.',
    },
    mostActiveDay: '{{day}} est le jour le plus actif.',
  },
  cards: {
    vitalityScore: {
      title: 'Score de vitalité',
      unit: '%',
      info: "Un score composite basé sur le sommeil, l'activité, la VFC, le stress et la fréquence cardiaque au repos.",
    },
    sleep: {
      title: 'Sommeil',
      unit: '',
      info: 'Durée moyenne de sommeil par nuit.',
    },
    stress: {
      title: 'Stress',
      unit: '%',
      info: 'Niveau de stress mesuré ; plus il est bas, plus c’est calme.',
    },
    recovery: {
      title: 'Récupération',
      unit: '%',
      info: 'À quel point le corps récupère bien, selon le sommeil, la VFC et le stress.',
    },
    activity: {
      title: 'Activité',
      unit: 'pas/jour',
      info: 'Nombre moyen de pas par jour.',
    },
    hrv: {
      title: 'VFC',
      unit: 'ms',
      info: 'Variabilité de la fréquence cardiaque — un indicateur de récupération et de résilience.',
    },
    restingHeartRate: {
      title: 'Fréquence cardiaque au repos',
      unit: 'bpm',
      info: 'La fréquence cardiaque la plus basse mesurée sur la période sélectionnée, comme approximation de la fréquence cardiaque au repos.',
    },
  },
  actions: {
    vitalityScore: {
      good: 'La vitalité évolue bien — conservez les routines actuelles.',
      opportunity:
        'La vitalité peut encore progresser — un court échange en équipe peut révéler ce qui freine.',
    },
    sleep: {
      good: "Le sommeil de l'équipe est sain — aucune action nécessaire.",
      opportunity:
        'Le sommeil est en dessous de la cible — envisagez un rappel sur la routine du soir et le temps d’écran avant le coucher.',
    },
    stress: {
      good: 'Le niveau de stress est maîtrisé — continuez à surveiller.',
      opportunity:
        'Le stress est élevé — un point rapide sur la charge de travail ou une activité de récupération peut aider.',
    },
    recovery: {
      good: "La récupération est solide — l'équipe récupère bien entre les efforts.",
      opportunity:
        "La récupération est en retard — vérifiez l'équilibre entre jours de repos et charge de travail.",
    },
    activity: {
      good: "Le niveau d'activité est sain — aucune action nécessaire.",
      opportunity:
        "L'activité est en dessous de la cible — une réunion en marchant ou un défi de pas peut aider.",
    },
    hrv: {
      good: 'La VFC est saine — aucune action nécessaire.',
      opportunity:
        'La VFC est plus basse que prévu — cela suit souvent le stress ou un mauvais sommeil ; à discuter ensemble.',
    },
    restingHeartRate: {
      good: 'La fréquence cardiaque au repos est dans une plage saine.',
      opportunity:
        'La fréquence cardiaque au repos est élevée — combinée au stress ou à un sommeil réduit, cela mérite un signalement.',
    },
  },
};
